// frontend/src/components/marketing/ProofSection.tsx
// LP-02: Tabbed proof section — Why Us | How | Tools
// Reduces cognitive overload with expandable detail cards

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles, Zap, Target, TrendingUp, Compass, Rocket,
  Link2, Shield, Clock, Users, HeartHandshake, LayoutDashboard, ListChecks,
  FileText, Lightbulb, BadgeCheck
} from 'lucide-react';
import '../../styles/marketing.css';

const TOOLS = [
  { 
    icon: Target, 
    key: 'conceptValidation', 
    descKey: 'conceptValidationDesc',
    color: 'var(--primary-400)', 
    path: '/concept-validator'
  },
  { 
    icon: TrendingUp, 
    key: 'impactEvaluator',
    descKey: 'impactEvaluatorDesc',
    color: 'var(--error-400)', 
    path: '/impact-evaluator' 
  },
  { 
    icon: Compass, 
    key: 'wayfinder',
    descKey: 'wayfinderDesc',
    color: 'var(--info-500)', 
    path: '/wayfinder' 
  },
  { 
    icon: Rocket, 
    key: 'gameChanger',
    descKey: 'gameChangerDesc',
    color: 'var(--warning-500)', 
    path: '/game-changer' 
  },
];

const STEPS_FOUNDER = [
  { icon: Lightbulb, titleKey: 'step1Title', descKey: 'step1Desc' },
  { icon: FileText,  titleKey: 'step2Title', descKey: 'step2Desc' },
  { icon: BadgeCheck, titleKey: 'step3Title', descKey: 'step3Desc' },
];

const STEPS_PROGRAM = [
  { icon: Lightbulb, titleKey: 'step1TitleProgram', descKey: 'step1DescProgram' },
  { icon: FileText,  titleKey: 'step2TitleProgram', descKey: 'step2DescProgram' },
  { icon: BadgeCheck, titleKey: 'step3TitleProgram', descKey: 'step3DescProgram' },
];

// Why Us expandable cards data
const WHY_US_CARDS_FOUNDER = [
  { 
    id: 'sources', 
    icon: Link2, 
    titleKey: 'whyUsSourcesTitle', 
    descKey: 'whyUsSourcesDesc',
    color: 'var(--primary-400)'
  },
  { 
    id: 'decisions', 
    icon: Shield, 
    titleKey: 'whyUsDecisionsTitle', 
    descKey: 'whyUsDecisionsDesc',
    color: 'var(--success-500)'
  },
  { 
    id: 'tracking', 
    icon: Clock, 
    titleKey: 'whyUsTrackingTitle', 
    descKey: 'whyUsTrackingDesc',
    color: 'var(--info-500)'
  },
  { 
    id: 'programs', 
    icon: Users, 
    titleKey: 'whyUsProgramsTitle', 
    descKey: 'whyUsProgramsDesc',
    color: 'var(--warning-500)'
  },
];

const WHY_US_CARDS_PROGRAM = [
  {
    id: 'cohort',
    icon: LayoutDashboard,
    titleKey: 'programWhyReadinessTitle',
    descKey: 'programWhyReadinessDesc',
    color: 'var(--primary-400)',
  },
  {
    id: 'interventions',
    icon: ListChecks,
    titleKey: 'programWhyInterventionsTitle',
    descKey: 'programWhyInterventionsDesc',
    color: 'var(--success-500)',
  },
  {
    id: 'privacy',
    icon: Shield,
    titleKey: 'programWhyPrivacyTitle',
    descKey: 'programWhyPrivacyDesc',
    color: 'var(--info-500)',
  },
  {
    id: 'support',
    icon: HeartHandshake,
    titleKey: 'programWhySupportRolesTitle',
    descKey: 'programWhySupportRolesDesc',
    color: 'var(--warning-500)',
  },
];

const PROGRAM_PILOT_CAPABILITIES = [
  {
    icon: Rocket,
    titleKey: 'programPilotFounderPathTitle',
    descKey: 'programPilotFounderPathDesc',
    color: 'var(--primary-400)',
  },
  {
    icon: LayoutDashboard,
    titleKey: 'programPilotCohortViewTitle',
    descKey: 'programPilotCohortViewDesc',
    color: 'var(--success-500)',
  },
  {
    icon: HeartHandshake,
    titleKey: 'programPilotSupportContextTitle',
    descKey: 'programPilotSupportContextDesc',
    color: 'var(--warning-500)',
  },
];

interface ProofSectionProps {
  audienceMode?: 'founder' | 'program';
}

type TabType = 'why' | 'how' | 'tools';

const ProofSection: React.FC<ProofSectionProps> = ({ audienceMode = 'founder' }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('why');
  
  // Helper to get text based on audience mode
  const getAudienceText = (founderKey: string, programKey: string) => {
    return audienceMode === 'founder' ? t(founderKey) : t(programKey);
  };

  const tabs: { id: TabType; label: string; icon: typeof Sparkles }[] = [
    { id: 'why', label: t(audienceMode === 'founder' ? 'whyUsTab' : 'programValueTab'), icon: Sparkles },
    { id: 'how', label: t('howTab'), icon: Zap },
    { id: 'tools', label: t(audienceMode === 'founder' ? 'toolsTab' : 'programPilotTab'), icon: Target },
  ];

  const whyCards = audienceMode === 'founder' ? WHY_US_CARDS_FOUNDER : WHY_US_CARDS_PROGRAM;

  return (
    <section className="proof-section">
      <div className="proof-content">

        {/* ── TAB NAVIGATION ── */}
        <div className="proof-tabs" role="tablist" aria-label={t('landingProofNavigationLabel')}>
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                type="button"
                role="tab"
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`proof-tab-btn ${isActive ? 'proof-tab-btn--active' : ''}`}
                id={`landing-proof-tab-${tab.id}`}
                aria-controls={`landing-proof-panel-${tab.id}`}
                aria-selected={isActive}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* ── TAB CONTENT ── */}
        <div className="proof-tab-content">
          
          {/* WHY US TAB */}
          {activeTab === 'why' && (
            <div
              className="proof-tab-panel"
              role="tabpanel"
              id="landing-proof-panel-why"
              aria-labelledby="landing-proof-tab-why"
            >
              {/* 2×2 Cards Grid — always visible, no accordion */}
              <div className="proof-cards-grid">
                {whyCards.map((card) => {
                  const Icon = card.icon;
                  return (
                    <div
                      key={card.id}
                      className="proof-card"
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = card.color;
                        e.currentTarget.style.boxShadow = `0 4px 20px ${card.color}18`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = 'var(--border-primary)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      <div className="proof-card-header">
                        <div className="proof-card-icon" style={{ background: `color-mix(in srgb, ${card.color} 12%, transparent)` }}>
                          <Icon size={18} color={card.color} />
                        </div>
                        <span className="proof-card-title">
                          {t(card.titleKey)}
                        </span>
                      </div>
                      <p className="proof-card-desc">
                        {t(card.descKey)}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* HOW TAB */}
          {activeTab === 'how' && (
            <div
              className="proof-tab-panel"
              role="tabpanel"
              id="landing-proof-panel-how"
              aria-labelledby="landing-proof-tab-how"
            >
              <div className="proof-section-header">
                <h3 className="proof-section-title">
                  {getAudienceText('howItWorksTitle', 'howItWorksTitleProgram')}
                </h3>
                <p className="proof-section-subtitle">
                  {getAudienceText('howItWorksSubtitle', 'howItWorksSubtitleProgram')}
                </p>
              </div>

              {/* Steps */}
              <div className="proof-steps-grid">
                {(audienceMode === 'founder' ? STEPS_FOUNDER : STEPS_PROGRAM).map((step, i) => {
                  const Icon = step.icon;
                  return (
                    <div key={i} className="proof-step-card">
                      {/* Step number */}
                      <div className="proof-step-number">
                        {i + 1}
                      </div>
                      
                      <div className="proof-step-icon">
                        <Icon size={22} color="var(--primary-500)" />
                      </div>
                      
                      <h4 className="proof-step-title">
                        {t(step.titleKey)}
                      </h4>
                      <p className="proof-step-desc">
                        {t(step.descKey)}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* TOOLS TAB */}
          {activeTab === 'tools' && (
            <div
              className="proof-tab-panel"
              role="tabpanel"
              id="landing-proof-panel-tools"
              aria-labelledby="landing-proof-tab-tools"
            >
              <div className="proof-section-header">
                <h3 className="proof-section-title">
                  {t(audienceMode === 'founder' ? 'featuresTitle' : 'programPilotTitle')}
                </h3>
                <p className="proof-section-subtitle">
                  {t(audienceMode === 'founder' ? 'featuresSubtitle' : 'programPilotSubtitle')}
                </p>
              </div>

              <div className="proof-tools-grid">
                {audienceMode === 'founder' ? TOOLS.map((tool) => {
                  const Icon = tool.icon;
                  return (
                    <button
                      type="button"
                      key={tool.key}
                      onClick={() => navigate(tool.path)}
                      className="proof-tool-card"
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = tool.color;
                        e.currentTarget.style.boxShadow = `0 6px 20px ${tool.color}30`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = 'var(--border-primary)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      <div className="proof-tool-icon" style={{ background: `color-mix(in srgb, ${tool.color} 12%, transparent)` }}>
                        <Icon size={22} color={tool.color} />
                      </div>
                      <div>
                        <h4 className="proof-tool-title">
                          {t(tool.key)}
                        </h4>
                        <p className="proof-tool-desc">
                          {t(tool.descKey)}
                        </p>
                      </div>
                    </button>
                  );
                }) : PROGRAM_PILOT_CAPABILITIES.map((capability) => {
                  const Icon = capability.icon;
                  return (
                    <div key={capability.titleKey} className="proof-tool-card proof-tool-card--static">
                      <div className="proof-tool-icon" style={{ background: `color-mix(in srgb, ${capability.color} 12%, transparent)` }}>
                        <Icon size={22} color={capability.color} />
                      </div>
                      <div>
                        <h4 className="proof-tool-title">{t(capability.titleKey)}</h4>
                        <p className="proof-tool-desc">{t(capability.descKey)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* ── TRUST BADGES (Bottom) ── */}
        <div className="proof-trust-bar">
          {(audienceMode === 'founder' ? [
            { icon: Shield, text: t('trustNoAITraining') },
            { icon: FileText, text: t('trustPrivacyFirst') },
            { icon: Clock, text: t('trustTwoMinResults') }
          ] : [
            { icon: Shield, text: t('programTrustFounderPrivacy') },
            { icon: Users, text: t('programTrustScopedPilot') },
            { icon: HeartHandshake, text: t('programTrustSupportContext') },
          ]).map((badge, i) => {
            const Icon = badge.icon;
            return (
              <div key={i} className="proof-trust-item">
                <Icon size={14} color="var(--primary-500)" />
                {badge.text}
              </div>
            );
          })}
        </div>

      </div>

    </section>
  );
};

export default ProofSection;
